import { useTranslation } from "react-i18next";

import { LinkDeviceMenuRow } from "../../../features/devices/LinkDeviceMenuRow";
import { SandboxMenuRow } from "../../../features/sandbox/SandboxToggle";
import { getUserDisplayName } from "../../../utils/userIdentity";
import type { ThemeMode, UserInfo } from "../AppsSidebar/types";
import UserInitials from "../UserInitials";

/**
 * Quién soy y qué puedo cambiar de mí.
 *
 * ── Por qué se separa de las aplicaciones ───────────────────────────────────
 * Estaba al pie del cajón de apps, debajo del catálogo. Eso obliga a abrir la
 * lista de destinos para cambiar el tema o cerrar sesión, que no son destinos.
 * Y al revés: el avatar de la cabecera llevaba encima las iniciales, un punto
 * rojo de avisos y un galón de cuadrícula —tres significados en 32 px— y
 * pulsarlo abría siempre lo mismo.
 *
 * Un control, una cosa. El avatar abre la cuenta.
 */

interface Props {
  usuario?: UserInfo;
  empresa?: string;
  rol?: string;
  tema: ThemeMode;
  onTema?: (t: ThemeMode) => void;
  onPerfil?: () => void;
  onCerrarSesion?: () => void;
  cerrandoSesion?: boolean;
}

const TEMAS: { id: ThemeMode; icono: string; clave: string }[] = [
  { id: "light", icono: "ri-sun-line", clave: "theme.light" },
  { id: "dark", icono: "ri-moon-line", clave: "theme.dark" },
  { id: "system", icono: "ri-computer-line", clave: "theme.system" },
];

export default function MenuDeCuenta({
  usuario,
  empresa,
  rol,
  tema,
  onTema,
  onPerfil,
  onCerrarSesion,
  cerrandoSesion = false,
}: Props) {
  const { t, i18n } = useTranslation("common");

  const nombre = getUserDisplayName(usuario) || t("general.user");

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-on-primary">
          <UserInitials user={usuario} iconClassName="text-lg" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-content">
            {nombre}
          </span>
          <span className="block truncate text-[11px] text-content-muted">
            {usuario?.email}
          </span>
          {(empresa || rol) && (
            <span className="block truncate text-[11px] text-content-faint">
              {empresa ? `${empresa} · ${rol}` : rol}
            </span>
          )}
        </span>
      </div>

      <div className="h-px bg-line-soft" aria-hidden="true" />

      <nav className="flex flex-col py-1">
        <button
          type="button"
          onClick={onPerfil}
          className="flex min-h-[44px] items-center gap-3 px-4 text-left text-sm text-content-secondary transition-colors hover:bg-subtle hover:text-content"
        >
          <i className="ri-user-line text-[15px] text-content-muted" aria-hidden="true" />
          {t("appsSidebar.myProfile", "Mi perfil")}
        </button>

        {/*
          El tema es un grupo de tres, no un interruptor: «sistema» es una
          opción real y con un interruptor de dos no se puede volver a ella.
        */}
        <div className="flex min-h-[44px] items-center justify-between gap-3 px-4">
          <span className="flex items-center gap-3 text-sm text-content-secondary">
            <i className="ri-contrast-2-line text-[15px] text-content-muted" aria-hidden="true" />
            {t("theme.title", "Tema")}
          </span>
          <span className="flex items-center gap-px rounded-md bg-subtle p-0.5" role="group">
            {TEMAS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onTema?.(o.id)}
                aria-pressed={tema === o.id}
                aria-label={t(o.clave)}
                title={t(o.clave)}
                className={`flex h-7 w-8 items-center justify-center rounded-sm transition-colors ${
                  tema === o.id
                    ? "bg-card text-content shadow-sm"
                    : "text-content-faint hover:text-content-secondary"
                }`}
              >
                <i className={`${o.icono} text-[14px]`} aria-hidden="true" />
              </button>
            ))}
          </span>
        </div>

        <div className="flex min-h-[44px] items-center justify-between gap-3 px-4">
          <span className="flex items-center gap-3 text-sm text-content-secondary">
            <i className="ri-translate-2 text-[15px] text-content-muted" aria-hidden="true" />
            {t("language.title", "Idioma")}
          </span>
          <span className="flex items-center gap-px rounded-md bg-subtle p-0.5" role="group">
            {["es", "en"].map((idioma) => (
              <button
                key={idioma}
                type="button"
                onClick={() => i18n.changeLanguage(idioma)}
                aria-pressed={i18n.language.startsWith(idioma)}
                className={`hud-dato flex h-7 w-8 items-center justify-center rounded-sm text-[11px] uppercase transition-colors ${
                  i18n.language.startsWith(idioma)
                    ? "bg-card text-content shadow-sm"
                    : "text-content-faint hover:text-content-secondary"
                }`}
              >
                {idioma}
              </button>
            ))}
          </span>
        </div>

        {/* Datos de ejemplo a voluntad; sólo la ve quien puede cambiarlo. */}
        <SandboxMenuRow />
        {/* Vincular un equipo (CC-CONSOLE#1523); mismo criterio que el Sandbox. */}
        <LinkDeviceMenuRow />
      </nav>

      <div className="h-px bg-line-soft" aria-hidden="true" />

      <button
        type="button"
        onClick={onCerrarSesion}
        disabled={cerrandoSesion}
        className="flex min-h-[44px] items-center gap-3 px-4 text-left text-sm text-error transition-colors hover:bg-error/10 disabled:opacity-60"
      >
        <i
          className={cerrandoSesion ? "ri-loader-4-line animate-spin" : "ri-logout-box-r-line"}
          aria-hidden="true"
        />
        {t("appsSidebar.logout", "Cerrar sesión")}
      </button>
    </div>
  );
}
